import { styled } from '@material-ui/core';

import { down } from '../../../assets/style/breakpointHelpers';

export const LandingPageWrapper = styled('div')({
	minHeight: '100vh',
	paddingBottom: '64px',
	backgroundColor: 'var(--color-bg-primary)',
	transition: 'background-color var(--transition-normal)',
});

/* ============================================================
   Sticky landing header (prototype: logo · nav · Apply now)
   ============================================================ */
export const LandingHeader = styled('header')({
	'position': 'sticky',
	'top': 0,
	'zIndex': 40,
	'display': 'flex',
	'alignItems': 'center',
	'justifyContent': 'space-between',
	'gap': 12,
	'padding': '16px clamp(18px, 4vw, 52px)',
	'background': 'color-mix(in srgb, var(--color-bg-primary) 92%, transparent)',
	'backdropFilter': 'blur(10px)',
	'WebkitBackdropFilter': 'blur(10px)',
	'borderBottom': '1px solid color-mix(in srgb, var(--color-text-primary) 6%, transparent)',

	'& .brand': {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		background: 'none',
		border: 'none',
		padding: 0,
		cursor: 'pointer',
	},

	'& .brand-mark': {
		width: 34,
		height: 34,
		objectFit: 'contain',
		flex: 'none',
	},

	'& .brand-name': {
		fontFamily: 'var(--font-family-display)',
		fontSize: 22,
		letterSpacing: '-0.01em',
		color: 'var(--color-text-primary)',
	},

	'& .nav': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},

	'& .nav-link': {
		'background': 'none',
		'border': 'none',
		'fontFamily': 'inherit',
		'fontSize': 14,
		'fontWeight': 500,
		'letterSpacing': '0.01em',
		'color': 'var(--color-text-secondary)',
		'padding': '12px 14px',
		'cursor': 'pointer',
		'transition': 'color var(--transition-fast)',
		'&:hover': { color: 'var(--color-brand-primary)' },
	},

	'& .apply-cta': {
		'background': 'var(--color-brand-primary)',
		'color': '#fff',
		'padding': '12px 24px',
		'borderRadius': 'var(--radius-full)',
		'fontWeight': 600,
		'letterSpacing': '0.01em',
		'fontSize': 14,
		'border': 'none',
		'fontFamily': 'inherit',
		'cursor': 'pointer',
		'minHeight': 44,
		'transition': 'background var(--transition-fast), transform var(--transition-fast)',
		'&:hover': { background: 'var(--color-brand-primary-hover, #255e38)', transform: 'translateY(-1px)' },
	},

	[down('md')]: {
		'& .nav .nav-link': { display: 'none' },
	},
});

export const LandingPageContent = styled('div')({
	maxWidth: '1280px',
	width: '100%',
	margin: '0 auto',
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	boxSizing: 'border-box',
});

/* ============================================================
   Hero — deep-green rounded card (the signature element)
   ============================================================ */
export const LandingPageHero = styled('section')({
	'position': 'relative',
	'overflow': 'hidden',
	'margin': '14px clamp(12px, 2.5vw, 24px) 0',
	'padding': 'clamp(30px, 5vw, 60px) clamp(22px, 4.5vw, 56px)',
	'borderRadius': 24,
	// Fixed deep-green billboard — identical in light & dark (does not use
	// theme tokens that would flip the surface/text in dark mode).
	'background': '#1e5631',
	'display': 'grid',
	'gridTemplateColumns': 'minmax(0, 1.05fr) minmax(0, 0.95fr)',
	'gap': 'clamp(24px, 4vw, 48px)',
	'alignItems': 'center',

	// floating cream circle decoration
	'&::after': {
		content: '""',
		position: 'absolute',
		right: '-80px',
		bottom: '-120px',
		width: 340,
		height: 340,
		borderRadius: '50%',
		background: 'rgba(247, 244, 236, 0.06)',
		pointerEvents: 'none',
	},

	'& .hero-copy': {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		gap: 20,
		zIndex: 1,
		animation: 'heroFadeUp 600ms cubic-bezier(0.22, 1, 0.36, 1) both',
	},

	'& .eyebrow': {
		fontSize: 11.5,
		fontWeight: 600,
		color: 'var(--color-brand-mint, #a8d5b4)',
		letterSpacing: '0.26em',
		textTransform: 'uppercase',
	},

	'& h1': {
		'margin': 0,
		'fontFamily': 'var(--font-family-display)',
		'fontWeight': 400,
		'fontSize': 'clamp(42px, 5.5vw, 68px)',
		'lineHeight': 1.02,
		'letterSpacing': '-0.01em',
		'color': '#f7f4ec',
		'textWrap': 'balance',

		'& em': {
			fontStyle: 'italic',
			color: 'var(--color-brand-accent)',
		},
	},

	'& .subcopy': {
		'margin': 0,
		'fontSize': 'clamp(15px, 1.5vw, 17px)',
		'lineHeight': 1.62,
		'color': 'rgba(247, 244, 236, 0.82)',
		'maxWidth': 460,

		'& strong': {
			color: 'var(--color-brand-accent)',
			fontWeight: 600,
			fontVariantNumeric: 'tabular-nums',
		},
	},

	'& .hero-cta': {
		display: 'flex',
		gap: 12,
		marginTop: 6,
		flexWrap: 'wrap',
	},

	'& .btn-primary': {
		'background': 'var(--color-brand-accent)',
		'color': '#1c2a20',
		'padding': '15px 30px',
		'borderRadius': 'var(--radius-full)',
		'fontWeight': 600,
		'letterSpacing': '0.01em',
		'fontSize': 15,
		'border': 'none',
		'fontFamily': 'inherit',
		'cursor': 'pointer',
		'minHeight': 44,
		'transition': 'filter var(--transition-fast), transform var(--transition-fast)',
		'&:hover': { filter: 'brightness(1.05)', transform: 'translateY(-1px)' },
	},

	'& .btn-ghost': {
		'background': 'none',
		'border': '1.5px solid rgba(247, 244, 236, 0.4)',
		'color': '#f7f4ec',
		'padding': '15px 30px',
		'borderRadius': 'var(--radius-full)',
		'fontWeight': 600,
		'letterSpacing': '0.01em',
		'fontSize': 15,
		'fontFamily': 'inherit',
		'cursor': 'pointer',
		'minHeight': 44,
		'transition': 'border-color var(--transition-fast)',
		'&:hover': { borderColor: 'rgba(247, 244, 236, 0.8)' },
	},

	// photo mosaic + stat card
	'& .hero-media': {
		position: 'relative',
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 14,
		zIndex: 1,
		animation: 'heroFadeIn 800ms cubic-bezier(0.22, 1, 0.36, 1) 200ms both',
	},

	'& .media-tall': {
		'height': 240,
		'borderRadius': 18,
		'overflow': 'hidden',
		'background': 'repeating-linear-gradient(45deg, #2c6b41 0 12px, #255e38 12px 24px)',

		'& img': {
			width: '100%',
			height: '100%',
			objectFit: 'cover',
			display: 'block',
		},
	},

	'& .media-col': {
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
	},

	'& .media-short': {
		height: 113,
		borderRadius: 18,
		background: 'repeating-linear-gradient(45deg, #2c6b41 0 12px, #255e38 12px 24px)',
	},

	'& .media-stat': {
		'height': 113,
		'borderRadius': 18,
		'background': '#ffffff',
		'display': 'flex',
		'flexDirection': 'column',
		'alignItems': 'center',
		'justifyContent': 'center',
		'gap': 2,

		'& .stat-num': {
			fontFamily: 'var(--font-family-display)',
			fontSize: 32,
			color: '#1e5631',
			fontVariantNumeric: 'tabular-nums',
			lineHeight: 1,
		},

		'& .stat-label': {
			fontSize: 12,
			color: '#4a5a4f',
			fontWeight: 500,
			letterSpacing: '0.02em',
		},
	},

	[down('md')]: {
		'gridTemplateColumns': 'minmax(0, 1fr)',
		'& .hero-media': { display: 'none' },
	},
});

/* ============================================================
   Elections section
   ============================================================ */
export const LandingPageAvailableElections = styled('div')({
	'display': 'flex',
	'flexDirection': 'column',
	'gap': 22,
	'padding': 'clamp(36px, 5vw, 56px) clamp(18px, 4vw, 52px) 0',

	'& .elections-head': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		flexWrap: 'wrap',
	},

	'& h2': {
		margin: 0,
		fontFamily: 'var(--font-family-display)',
		fontWeight: 400,
		fontSize: 'clamp(28px, 3.4vw, 38px)',
		letterSpacing: '-0.01em',
		color: 'var(--color-text-primary)',
	},

	'& .content': {
		display: 'grid',
		gap: 18,
		gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
	},

	[down('sm')]: {
		'& .content': { gridTemplateColumns: '1fr' },
	},
});

export const NoResultsMessage = styled('div')({
	gridColumn: '1 / -1',
	textAlign: 'center',
	padding: '48px 24px',
	color: 'var(--color-text-tertiary)',
	fontFamily: 'var(--font-family-regular)',
	fontSize: '15px',
	backgroundColor: 'var(--color-bg-elevated)',
	borderRadius: 20,
	boxShadow: 'var(--shadow-sm)',
	animation: 'heroFadeUp 500ms cubic-bezier(0.22, 1, 0.36, 1) both',
});

export const ElectionSearchContainer = styled('div')({
	'display': 'flex',
	'justifyContent': 'center',

	'& .search-wrapper': {
		'position': 'relative',
		'width': '100%',
		'maxWidth': '400px',

		'& input': {
			'width': '100%',
			'height': '44px',
			'padding': '0 20px',
			'borderRadius': 'var(--radius-full)',
			'border': '1px solid var(--color-border-default)',
			'backgroundColor': 'var(--color-bg-elevated)',
			'color': 'var(--color-text-primary)',
			'fontFamily': 'var(--font-family-regular)',
			'fontSize': 'var(--text-body-size)',
			'outline': 'none',
			'transition': 'border-color var(--transition-fast)',

			'&::placeholder': { color: 'var(--color-text-tertiary)' },
			'&:focus': { borderColor: 'var(--color-brand-primary)' },
		},
	},
});

/* ============================================================
   Election card (prototype: pill CTA, type pill, status dot)
   ============================================================ */
export const AvailableElectionCard = styled('div')({
	'width': '100%',
	'position': 'relative',
	'boxSizing': 'border-box',
	'display': 'flex',
	'flexDirection': 'column',
	'gap': 14,
	'padding': '26px',
	'cursor': 'pointer',
	'borderRadius': 20,
	'backgroundColor': 'var(--color-bg-elevated)',
	'boxShadow': 'var(--shadow-sm)',
	'animation': 'heroFadeUp 500ms cubic-bezier(0.22, 1, 0.36, 1) both',
	'transition': 'transform var(--transition-fast), box-shadow var(--transition-fast)',

	'&:hover': {
		transform: 'translateY(-4px)',
		boxShadow: 'var(--shadow-lg)',
	},

	'&:active': { transform: 'translateY(-2px) scale(0.99)' },

	'&:focus-visible': {
		outline: '2px solid var(--color-brand-primary)',
		outlineOffset: 2,
	},

	'& .card-top': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 10,
	},

	'& .type-badge': {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: '0.1em',
		textTransform: 'uppercase',
		padding: '5px 12px',
		borderRadius: 'var(--radius-full)',
	},

	'& .status-label': {
		fontSize: 12,
		fontWeight: 500,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		whiteSpace: 'nowrap',
	},

	'& .status-label .dot': {
		width: 7,
		height: 7,
		borderRadius: '50%',
		background: 'currentColor',
	},

	'& h3': {
		margin: 0,
		fontFamily: 'var(--font-family-display)',
		fontWeight: 400,
		fontSize: 24,
		lineHeight: 1.15,
		color: 'var(--color-text-primary)',
		textTransform: 'capitalize',
	},

	'& .meta': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		fontSize: 13,
		color: 'var(--color-text-tertiary)',
		fontWeight: 500,
		fontVariantNumeric: 'tabular-nums',
	},

	'& .meta .dot': {
		width: 3,
		height: 3,
		borderRadius: '50%',
		background: 'var(--color-border-strong)',
	},

	'& .meta .with-icon': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
	},

	'& .card-cta': {
		marginTop: 'auto',
		padding: '13px',
		borderRadius: 'var(--radius-full)',
		fontWeight: 600,
		letterSpacing: '0.01em',
		fontSize: 14.5,
		textAlign: 'center',
		border: 'none',
		fontFamily: 'inherit',
		width: '100%',
		minHeight: 46,
		background: 'var(--color-brand-primary)',
		color: '#fff',
		transition: 'background var(--transition-fast)',
	},

	'&:hover .card-cta': { background: 'var(--color-brand-primary-hover, #255e38)' },

	'& .card-cta.is-disabled': {
		background: 'var(--color-bg-tertiary)',
		color: 'var(--color-text-tertiary)',
		cursor: 'default',
	},

	[down('md')]: { padding: '20px' },
});
